import React from 'react';
import { getBlockDefinition } from '../../automation/blockCatalog';
import {
  getActionConfigChips,
  getActionSubtitle,
} from '../../automation/workflowSummary';
import type { WorkflowValidationIssue } from '../../automation/workflowGraph';
import {
  getActionLabel,
  type AutomationActionType,
  type AutomationGraphNode,
} from '../../types/AutomationWorkflow.types';
import { BlockConfigForm } from './BlockConfigForm';
import {
  OptionChip,
  PathTokenHint,
  StepCardHeader,
  type StepDragHandlers,
  type StepRunStatus,
} from './WorkflowStepCardParts';

export const WorkflowActionCard: React.FC<{
  node: AutomationGraphNode;
  selected: boolean;
  issues: WorkflowValidationIssue[];
  status: StepRunStatus;
  drag: StepDragHandlers;
  onSelect: () => void;
  onRemove: () => void;
  onConfigChange: (config: Record<string, unknown>) => void;
}> = ({
  node,
  selected,
  issues,
  status,
  drag,
  onSelect,
  onRemove,
  onConfigChange,
}) => {
  const actionType = node.type as AutomationActionType;
  const definition = getBlockDefinition(actionType);
  const chips = getActionConfigChips(node);
  const isImport = actionType === 'import_to_library';

  const className = [
    'automation-step-card',
    'automation-step-card-action',
    selected ? 'selected' : '',
    issues.length ? 'invalid' : '',
    status.running ? 'running' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const importOptionChips = (
    <>
      <OptionChip
        label="Analyze BPM & key"
        active={node.config.analyzeBpmKey !== false}
        onToggle={() =>
          onConfigChange({ analyzeBpmKey: node.config.analyzeBpmKey === false })
        }
      />
      <OptionChip
        label="Flag duplicates"
        active={node.config.flagDuplicates !== false}
        onToggle={() =>
          onConfigChange({
            flagDuplicates: node.config.flagDuplicates === false,
          })
        }
      />
    </>
  );

  return (
    <article className={className} draggable {...drag}>
      <StepCardHeader
        actionType={actionType}
        title={getActionLabel(actionType)}
        subtitle={getActionSubtitle(node)}
        status={status}
        onSelect={onSelect}
        onRemove={onRemove}
      />

      {isImport && !selected && (
        <div className="automation-step-chips">{importOptionChips}</div>
      )}

      {!isImport && !selected && chips.length > 0 && (
        <div className="automation-step-chips">
          {chips.map((chip) => (
            <span
              key={`${chip.label}-${chip.value}`}
              className="automation-config-chip"
            >
              {chip.label} → {chip.value}
            </span>
          ))}
        </div>
      )}

      {selected && definition && (
        <div className="automation-step-inline-config">
          {isImport ? (
            <div className="automation-step-option-chips">
              {importOptionChips}
            </div>
          ) : (
            <BlockConfigForm
              variant="inline"
              node={node}
              onChange={onConfigChange}
            />
          )}
          {actionType === 'copy_move_file' && <PathTokenHint />}
        </div>
      )}

      {issues.length > 0 && (
        <p className="automation-field-error">{issues[0].message}</p>
      )}
    </article>
  );
};
